import type { AxiosRequestConfig } from "axios";

import { PATHS } from "../config/paths";
import { annaCallRobotApi } from "./annaCallRobotApi";
import { getApiProjectId, withProjectParams } from "./projectScope";

/**
 * Domain-facing API for the backend. UI code should use this instead of raw paths
 * or `annaCallRobotApi` HTTP methods so routes and verbs stay centralized.
 */

function scopedGet(url: string, params?: Record<string, unknown>) {
  const merged = withProjectParams(params ?? {});
  return Object.keys(merged).length
    ? annaCallRobotApi.get(url, { params: merged })
    : annaCallRobotApi.get(url);
}

function projectConfig(): AxiosRequestConfig | undefined {
  const id = getApiProjectId();
  return id == null ? undefined : { params: { project: id } };
}

function scopedPost(url: string, body?: object) {
  const config = projectConfig();
  return config
    ? annaCallRobotApi.post(url, body, config)
    : annaCallRobotApi.post(url, body);
}

function scopedPatch(url: string, body?: object) {
  const config = projectConfig();
  return config
    ? annaCallRobotApi.patch(url, body, config)
    : annaCallRobotApi.patch(url, body);
}

function scopedBlobGet(url: string) {
  const params = withProjectParams({});
  return annaCallRobotApi.get(url, {
    ...(Object.keys(params).length ? { params } : {}),
    responseType: "blob",
  });
}

function scopedDelete(url: string) {
  const config = projectConfig();
  return config
    ? annaCallRobotApi.delete(url, config)
    : annaCallRobotApi.delete(url);
}

export const annaCallRobotClient = {
  login: (body: { username: string; password: string }) =>
    annaCallRobotApi.post(PATHS.API.LOGIN, body),

  getSiteTexts: (config?: AxiosRequestConfig) =>
    annaCallRobotApi.get(PATHS.API.SITE_TEXTS, config),

  getAppSettings: () => annaCallRobotApi.get(PATHS.API.APP_SETTINGS),

  getRecordBundle: () => scopedGet(PATHS.API.RECORD_BUNDLE),

  startRecordBundle: (kinds: string[]) =>
    scopedPost(PATHS.API.RECORD_BUNDLE, { kinds }),

  downloadRecordBundle: (id: number) =>
    scopedBlobGet(PATHS.API.recordBundleFile(id)),

  patchAppSettings: (body: object) =>
    annaCallRobotApi.patch(PATHS.API.APP_SETTINGS, body),

  getGoogleCalendarSettings: () =>
    annaCallRobotApi.get(PATHS.API.GOOGLE_CALENDAR_SETTINGS),

  patchGoogleCalendarSettings: (body: object) =>
    annaCallRobotApi.patch(PATHS.API.GOOGLE_CALENDAR_SETTINGS, body),

  getGoogleCalendarOAuthStart: (frontendRoot?: string) =>
    annaCallRobotApi.get(PATHS.API.GOOGLE_CALENDAR_OAUTH_START, {
      params: frontendRoot ? { frontend_root: frontendRoot } : undefined,
    }),

  postGoogleCalendarPull: () =>
    annaCallRobotApi.post(PATHS.API.GOOGLE_CALENDAR_SYNC_PULL, {}),

  postGoogleCalendarPush: () =>
    annaCallRobotApi.post(PATHS.API.GOOGLE_CALENDAR_SYNC_PUSH, {}),

  listProjects: () => annaCallRobotApi.get(PATHS.API.PROJECTS),

  createProject: (body: { name?: string }) =>
    annaCallRobotApi.post(PATHS.API.PROJECTS, body),

  getProject: (id: number) => annaCallRobotApi.get(PATHS.API.project(id)),

  patchProject: (id: number, body: object) =>
    annaCallRobotApi.patch(PATHS.API.project(id), body),

  deleteProject: (id: number) => annaCallRobotApi.delete(PATHS.API.project(id)),

  getProjectStats: (id: number) =>
    annaCallRobotApi.get(PATHS.API.projectStats(id)),

  cloneProject: (id: number, body?: { name?: string }) =>
    annaCallRobotApi.post(PATHS.API.projectClone(id), body ?? {}),

  getProjectLive: (id: number, params: { fields: string; since?: string }) =>
    annaCallRobotApi.get(PATHS.API.projectLive(id), { params }),

  listDncPhoneChecks: (params?: {
    allProjects?: boolean;
    includeDisabled?: boolean;
  }) => {
    const query: Record<string, unknown> = {};
    if (params?.allProjects) {
      query.all_projects = "1";
    }
    if (params?.includeDisabled) {
      query.include_disabled = "1";
    }
    return scopedGet(PATHS.API.DNC_PHONE_CHECKS, query);
  },

  listUncheckedAltPhonesDnc: (params?: {
    includeDisabled?: boolean;
    allProjects?: boolean;
  }) => {
    const query: Record<string, unknown> = {};
    if (params?.includeDisabled) {
      query.include_disabled = "1";
    }
    if (params?.allProjects) {
      query.all_projects = "1";
    }
    const merged = withProjectParams(query);
    return Object.keys(merged).length
      ? annaCallRobotApi.get(PATHS.API.UNCHECKED_ALT_PHONES_DNC, {
          params: merged,
        })
      : annaCallRobotApi.get(PATHS.API.UNCHECKED_ALT_PHONES_DNC);
  },

  deleteDncPhoneCheck: (id: number) =>
    scopedDelete(PATHS.API.dncPhoneCheck(id)),

  checkNumberDnc: (body: { phone_number: string }) =>
    scopedPost(PATHS.API.CHECK_NUMBER_DNC, body),

  checkAltPhonesDncBulk: (body: {
    force_already_checked: boolean;
    limit: number;
  }) => scopedPost(PATHS.API.CHECK_ALT_PHONES_DNC, body),

  checkPropertyPhoneNumbersDnc: (body: {
    property_phone_number_ids: number[];
    all_projects?: boolean;
    include_disabled?: boolean;
    cache_only?: boolean;
  }) => scopedPost(PATHS.API.CHECK_PROPERTY_PHONES_DNC, body),

  checkContactAltPhonesDnc: (
    contactId: number,
    body: { force_already_checked: boolean; limit: number },
  ) => scopedPost(PATHS.API.propertyCheckAltPhonesDnc(contactId), body),

  getContactFilledPrompt: (contactId: number) =>
    scopedGet(PATHS.API.propertyFilledPrompt(contactId)),

  listContacts: (params: {
    fields: string;
    q?: string;
    limit?: number;
    show_all?: number;
  }) => scopedGet(PATHS.API.PROPERTY, params),

  getContact: (id: number) => scopedGet(PATHS.API.property(id)),

  createContact: (body: object) => scopedPost(PATHS.API.PROPERTY, body),

  updateContact: (id: number, body: object) =>
    scopedPatch(PATHS.API.property(id), body),

  deleteContact: (id: number) => scopedDelete(PATHS.API.property(id)),

  clearContactDnc: (id: number) =>
    scopedPost(PATHS.API.propertyClearDnc(id), {}),

  moveContactToProject: (id: number, body: { target_project_id: number }) =>
    scopedPost(PATHS.API.propertyMove(id), body),

  cloneContactToProject: (id: number, body: { target_project_id: number }) =>
    scopedPost(PATHS.API.propertyClone(id), body),

  getContactNextWindow: (projectId: number, propertyId: number) =>
    scopedGet(PATHS.API.propertyNextWindow(projectId, propertyId)),

  uploadContactsExcel: (formData: FormData) =>
    scopedPost(PATHS.API.PROPERTY_UPLOAD_EXCEL, formData),

  getPropertyExcelCount: () =>
    scopedGet(PATHS.API.PROPERTY_EXCEL, { count: 1 }),

  downloadPropertyExcel: () => scopedBlobGet(PATHS.API.PROPERTY_EXCEL),

  getCallsExcelCount: () => scopedGet(PATHS.API.CALLS_EXCEL, { count: 1 }),

  downloadCallsExcel: () => scopedBlobGet(PATHS.API.CALLS_EXCEL),

  uploadCallsExcel: (formData: FormData) =>
    scopedPost(PATHS.API.CALLS_EXCEL, formData),

  getDncExcelCount: () =>
    scopedGet(PATHS.API.DNC_PHONE_CHECKS_EXCEL, { count: 1 }),

  downloadDncExcel: () => scopedBlobGet(PATHS.API.DNC_PHONE_CHECKS_EXCEL),

  uploadDncExcel: (formData: FormData) =>
    scopedPost(PATHS.API.DNC_PHONE_CHECKS_EXCEL, formData),

  getWebhookExcelCount: () =>
    scopedGet(PATHS.API.WEBHOOK_LOGS_EXCEL, { count: 1 }),

  downloadWebhookExcel: () => scopedBlobGet(PATHS.API.WEBHOOK_LOGS_EXCEL),

  uploadWebhookExcel: (formData: FormData) =>
    scopedPost(PATHS.API.WEBHOOK_LOGS_EXCEL, formData),

  getContactsPatchPlaceholderCellsPreview: () =>
    scopedGet(PATHS.API.PROPERTY_PATCH_PLACEHOLDER_CELLS_PREVIEW),

  postContactsPatchPlaceholderCells: () =>
    scopedPost(PATHS.API.PROPERTY_PATCH_PLACEHOLDER_CELLS),

  getCalendar: (params: {
    start: string;
    end: string;
    allProjects?: boolean;
  }) => {
    const query: Record<string, unknown> = {
      start: params.start,
      end: params.end,
    };
    if (params.allProjects) {
      query.all_projects = "1";
    }
    return scopedGet(PATHS.API.CALENDAR, query);
  },

  createCalendarBooking: (body: object) =>
    annaCallRobotApi.post(PATHS.API.CALENDAR_BOOKINGS, body),

  updateCalendarBooking: (id: number, body: object) =>
    annaCallRobotApi.patch(PATHS.API.calendarBooking(id), body),

  getCallingCalendar: (
    projectId: number,
    params: { start: string; end: string },
  ) =>
    annaCallRobotApi.get(PATHS.API.callingCalendar(projectId), {
      params: { start: params.start, end: params.end },
    }),

  createCallingBlackout: (projectId: number, body: object) =>
    annaCallRobotApi.post(PATHS.API.callingCalendar(projectId), body),

  updateCallingBlackout: (projectId: number, id: number, body: object) =>
    annaCallRobotApi.patch(PATHS.API.callingCalendarEvent(projectId, id), body),

  getMakeCallsContext: (propertyId: number) =>
    scopedGet(PATHS.API.MAKE_CALLS, { property_id: propertyId }),

  makeCalls: (body: {
    phones: { property_id: number; number: string }[];
    delay_seconds: number;
    random_seconds: number;
    batch_mode: "separate" | "join_latest";
    from_numbers?: string[];
  }) => scopedPost(PATHS.API.MAKE_CALLS, body),

  listCalls: (params?: {
    fields?: string;
    preset?: string;
    status?: string;
    show_all?: number;
  }) =>
    params ? scopedGet(PATHS.API.CALLS, params) : scopedGet(PATHS.API.CALLS),

  getDialingActivity: (params?: { allProjects?: boolean }) => {
    const query: Record<string, unknown> = {};
    if (params?.allProjects) {
      query.all_projects = "1";
    }
    return scopedGet(PATHS.API.DIALING_ACTIVITY, query);
  },

  getPlanMeasurements: () => scopedGet(PATHS.API.PLAN_MEASUREMENTS),

  getCall: (id: number) => scopedGet(PATHS.API.call(id)),

  getCallForm: (id: number) => scopedGet(PATHS.API.callForm(id)),

  explainCallWhyFailed: (id: number, body?: { force?: boolean }) =>
    scopedPost(PATHS.API.callWhyFailed(id), body ?? {}),

  reclassifyCall: (id: number) => scopedPost(PATHS.API.callReclassify(id), {}),

  updateCall: (id: number, body: object) =>
    scopedPatch(PATHS.API.call(id), body),

  deleteCall: (id: number) => scopedDelete(PATHS.API.call(id)),

  deleteCalls: (ids: number[]) =>
    scopedPost(PATHS.API.CALLS_BULK_DELETE, { ids }),

  listWebhookLogs: (params?: {
    django_call_id?: number | string;
    include_bodies?: boolean | 0 | 1;
  }) =>
    params
      ? scopedGet(PATHS.API.WEBHOOK_LOGS, params)
      : scopedGet(PATHS.API.WEBHOOK_LOGS),

  getWebhookLog: (id: number) => scopedGet(PATHS.API.webhookLog(id)),

  createWebhookLog: (body: object) => scopedPost(PATHS.API.WEBHOOK_LOGS, body),

  updateWebhookLog: (id: number, body: object) =>
    scopedPatch(PATHS.API.webhookLog(id), body),

  deleteWebhookLog: (id: number) => scopedDelete(PATHS.API.webhookLog(id)),

  simulateWebhookLog: (body: object) =>
    scopedPost(PATHS.API.WEBHOOK_LOGS_SIMULATE, body),

  listSipTrunks: () => scopedGet(PATHS.API.SIP_TRUNKS),

  syncSipTrunksFromVapi: () =>
    scopedPost(PATHS.API.SIP_TRUNKS_SYNC_FROM_VAPI, {}),

  createSipTrunk: (body: object) => scopedPost(PATHS.API.SIP_TRUNKS, body),

  updateSipTrunk: (id: number, body: object) =>
    scopedPatch(PATHS.API.sipTrunk(id), body),

  deleteSipTrunk: (id: number) => scopedDelete(PATHS.API.sipTrunk(id)),

  createSipPhoneNumber: (trunkId: number, body: object) =>
    scopedPost(PATHS.API.sipTrunkPhoneNumbers(trunkId), body),

  updateSipPhoneNumber: (id: number, body: object) =>
    scopedPatch(PATHS.API.sipPhoneNumber(id), body),

  deleteSipPhoneNumber: (id: number) =>
    scopedDelete(PATHS.API.sipPhoneNumber(id)),

  testSipPhoneNumberCall: (id: number, body: { to_number: string }) =>
    scopedPost(PATHS.API.sipPhoneNumberTestCall(id), body),

  listVapiAssistants: () => scopedGet(PATHS.API.VAPI_ASSISTANTS),

  getVapiAssistantServerUrl: () =>
    scopedGet(PATHS.API.VAPI_ASSISTANT_SERVER_URL),

  patchVapiAssistantServerUrl: (body: { server_url: string }) =>
    scopedPatch(PATHS.API.VAPI_ASSISTANT_SERVER_URL, body),

  getVapiEmulatorSettings: () =>
    annaCallRobotApi.get(PATHS.API.VAPI_EMULATOR_SETTINGS),

  patchVapiEmulatorSettings: (body: object) =>
    annaCallRobotApi.patch(PATHS.API.VAPI_EMULATOR_SETTINGS, body),

  resetVapiEmulatorCursor: () =>
    annaCallRobotApi.post(PATHS.API.VAPI_EMULATOR_RESET_CURSOR, {}),

  listVapiEmulatorAnswers: () =>
    annaCallRobotApi.get(PATHS.API.VAPI_EMULATOR_ANSWERS),

  createVapiEmulatorAnswer: (body: object) =>
    annaCallRobotApi.post(PATHS.API.VAPI_EMULATOR_ANSWERS, body),

  patchVapiEmulatorAnswer: (id: number, body: object) =>
    annaCallRobotApi.patch(PATHS.API.vapiEmulatorAnswer(id), body),

  deleteVapiEmulatorAnswer: (id: number) =>
    annaCallRobotApi.delete(PATHS.API.vapiEmulatorAnswer(id)),

  reorderVapiEmulatorAnswers: (ids: number[]) =>
    annaCallRobotApi.post(PATHS.API.VAPI_EMULATOR_ANSWERS_REORDER, { ids }),

  listVapiEmulatorLogs: (params?: { limit?: number }) =>
    annaCallRobotApi.get(PATHS.API.VAPI_EMULATOR_LOGS, { params }),

  clearVapiEmulatorLogs: () =>
    annaCallRobotApi.delete(PATHS.API.VAPI_EMULATOR_LOGS),

  listVapiEmulatorOngoingCalls: () =>
    annaCallRobotApi.get(PATHS.API.VAPI_EMULATOR_ONGOING_CALLS),

  sendVapiEmulatorWebhook: (body: object) =>
    annaCallRobotApi.post(PATHS.API.VAPI_EMULATOR_SEND_WEBHOOK, body),
};
